import type { InputHTMLAttributes, ReactNode } from 'react';
import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { ShellIcons, shellMedia, shellMetrics, tokens } from '@vigeo/ui';

/**
 * One field that holds its chosen values as pills and a search box after them (Figma "Concept A v2" frames 6, 8, 9
 * and 2): a pill is its words and an ×, "Add another" follows them, and a search icon sits at the right. Used by the
 * place picker and by the roster's Service area filter, so the two cannot drift apart.
 *
 * It draws and nothing more: the caller owns what is chosen, what the box does and the list that drops from it.
 */
const useStyles = makeStyles({
  root: { position: 'relative', width: '100%' },
  field: {
    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: tokens.spacingHorizontalXS, minHeight: shellMetrics.controlHeight, boxSizing: 'border-box', width: '100%',
    paddingBlock: tokens.spacingVerticalXXS, paddingInline: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground1, border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`, borderRadius: tokens.borderRadiusMedium,
    // The focus ring is drawn on this box: the input inside it has none of its own, and the browser sweep's focus-ring probe looks at the element and its parent.
    ':focus-within': { boxShadow: `0 0 0 ${tokens.strokeWidthThin} ${tokens.colorBrandForeground1}`, outline: `${tokens.strokeWidthThin} solid ${tokens.colorBrandForeground1}` },
  },

  disabled: { backgroundColor: tokens.colorNeutralBackgroundDisabled },
  pill: {
    display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXXS, maxWidth: '100%',
    paddingBlock: tokens.spacingVerticalXXS, paddingInlineStart: tokens.spacingHorizontalS, paddingInlineEnd: tokens.spacingHorizontalXXS,
    backgroundColor: tokens.colorNeutralBackground2, border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusSmall,
    fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, color: tokens.colorNeutralForeground1, overflowWrap: 'anywhere',
  },
  pills: { display: 'contents', listStyleType: 'none', margin: 0, padding: 0 },
  /**
   * ONE LINE, FOR A FIELD THAT STANDS IN A ROW OF OTHER CONTROLS (#669).
   *
   * Wrapping is right for the place picker, which is a field on its own and may hold several
   * places: frames 6, 8 and 9 draw the pills running onto further lines. It is wrong for the
   * roster's Place filter, which shares a line with Search and two dropdowns — there, a pill
   * long enough to push the search box onto a second line made the box 16-28pt taller than its
   * neighbours and the row stopped being a row (between 1040 and 1100 wide).
   *
   * So the pill gives way instead of the box growing: it shrinks and its words clip. That is
   * what `.field`'s own promise above requires — the height of the controls beside it, with or
   * without a pill — and with 52-character names in the list some clipping is unavoidable
   * whatever else is chosen. The full words stay in the DOM, in the x's label, and on hover.
   */
  fieldOneLine: { flexWrap: 'nowrap' },
  pillShrinks: { minWidth: 0, flexShrink: 1 },
  labelClips: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  /**
   * The box is the height of the controls beside it (`controlHeight`) with or without a pill, at every width down to the phone
   * width (#544, N10). On a phone (`shellMedia.phone`, where the app's touch size applies and the browser sweep checks it) the
   * pill's x and the search box are each a touch target, so the box is taller there.
   *
   * The x is a plain button, not a Fluent Button: the theme gives every Fluent Button a minimum height of `controlHeight`, which
   * made a pill taller than the box that holds it.
   */
  x: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxSizing: 'border-box',
    width: tokens.spacingHorizontalXXL, height: tokens.spacingVerticalXXL, padding: 0, border: 'none', borderRadius: tokens.borderRadiusSmall,
    backgroundColor: 'transparent', color: tokens.colorNeutralForeground2, cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
    ':focus-visible': { outline: `${tokens.strokeWidthThick} solid ${tokens.colorBrandForeground1}` },
    [shellMedia.phone]: { width: shellMetrics.touchTarget, height: shellMetrics.touchTarget },
  },
  input: {
    flexGrow: 1, flexBasis: '4rem', minWidth: '4rem', border: 'none', outline: 'none', backgroundColor: 'transparent',
    fontFamily: 'inherit', fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, color: tokens.colorNeutralForeground1,
    paddingBlock: tokens.spacingVerticalXS,
    [shellMedia.phone]: { minHeight: shellMetrics.touchTarget },
    '::placeholder': { color: tokens.colorNeutralForeground3 },
  },
  icon: { color: tokens.colorNeutralForeground2, display: 'inline-flex', flexShrink: 0 },
});

export interface Pill {
  readonly key: string;
  readonly label: string;
  readonly onRemove: () => void;
}

export interface PillFieldProps {
  /** The chosen values, in order. */
  readonly pills: readonly Pill[];
  /** What each pill's × says to assistive technology, given the pill's words. */
  readonly removeLabel: (label: string) => string;
  /** Names the list of pills for assistive technology. */
  readonly pillsLabel: string;
  /** The search box's own attributes: value, handlers, the combobox roles. */
  readonly input: InputHTMLAttributes<HTMLInputElement>;
  readonly disabled?: boolean;
  /**
   * Keep the box one line tall, letting a long pill shrink and clip rather than wrap (#669).
   *
   * For a field standing in a row of other controls, where the box growing breaks the row. The
   * place picker leaves this off: it is a field on its own and its pills are meant to wrap.
   */
  readonly oneLine?: boolean;
  /** The list that drops from the box; the caller decides when it is there. */
  readonly children?: ReactNode;
}

export function PillField({ pills, removeLabel, pillsLabel, input, disabled = false, oneLine = false, children }: PillFieldProps) {
  const styles = useStyles();
  return (
    <div className={styles.root}>
      <div className={mergeClasses(styles.field, oneLine && styles.fieldOneLine, disabled && styles.disabled)}>
        {pills.length === 0 ? null : (
          <ul className={styles.pills} aria-label={pillsLabel}>
            {pills.map((pill) => (
              <li key={pill.key} className={mergeClasses(styles.pill, oneLine && styles.pillShrinks)}>
                {/* `title` only where the words can actually be clipped, so it is not hover noise everywhere else. */}
                <span className={oneLine ? styles.labelClips : undefined} {...(oneLine ? { title: pill.label } : {})}>{pill.label}</span>
                <button type="button" className={styles.x} aria-label={removeLabel(pill.label)} onClick={pill.onRemove}><ShellIcons.close /></button>
              </li>
            ))}
          </ul>
        )}
        <input className={styles.input} disabled={disabled} {...input} />
        <span className={styles.icon} aria-hidden="true"><ShellIcons.search /></span>
      </div>
      {children}
    </div>
  );
}
